import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { Flame, MessageCircleMore, MessageCirclePlus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import type { DropdownProps } from 'semantic-ui-react';
import { Button, Dropdown, Popup } from 'semantic-ui-react';

import { unwrapQueryErrorMessage } from '@store/api';
import { useModal } from '@components/modal';
import {
  useHasAllPermissions,
  useHasAnyPermission,
} from '@components/permissions';
import { PopupPanel } from '@components/popup';
import raiseToast from '@components/toast';

import type {
  VexStatus,
  VulnerabilityPackage,
} from '../../applications.models';
import { useUpsertVersionVulnerabilityAnnotationMutation } from '../../utils';

import { VulnerabilityAnnotationDetails } from './vulnerability-annotation-details';
import { VEX_STATUS_OPTIONS } from './vulnerability-annotation.const';
import s from './vulnerability-annotation-controls.scss';

export interface VulnerabilityAnnotationControlsProps {
  appId: string;
  pkg: VulnerabilityPackage;
  versionId: string;
  vulnerabilityId: string;
}

const EDIT_DENIED_TOOLTIP =
  'You do not have permission to edit VEX annotations.';

export const VulnerabilityAnnotationControls = ({
  appId,
  pkg,
  versionId,
  vulnerabilityId,
}: VulnerabilityAnnotationControlsProps) => {
  useStyles(s);

  const canEdit = useHasAnyPermission(
    'createVulnAnnotation',
    'updateVulnAnnotation',
    'deleteVulnAnnotation',
  );
  const canViewDetails = useHasAllPermissions('getVulnAnnotation');

  const annotationId = pkg.vulnerabilityAnnotationId ?? undefined;
  const isAnnotated = !!annotationId;

  const { open: openUpsert } = useModal('vulnerabilityAnnotationUpsert');
  const { open: openDelete } = useModal('vulnerabilityAnnotationDelete');

  const [upsertAnnotation, { isLoading: isUpserting }] =
    useUpsertVersionVulnerabilityAnnotationMutation();

  const packageRef = useMemo(
    () => ({ name: pkg.name, type: pkg.type, version: pkg.version }),
    [pkg.name, pkg.type, pkg.version],
  );

  const handleStatusChange = useCallback(
    async (_: unknown, { value }: DropdownProps) => {
      const nextStatus = (value ?? null) as VexStatus | null;

      if (nextStatus === null) {
        if (annotationId) {
          openDelete({ annotationId, appId, versionId, vulnerabilityId });
        }
        return;
      }

      const result = await upsertAnnotation({
        annotationId,
        appId,
        packageName: packageRef.name,
        packageType: packageRef.type,
        packageVersion: packageRef.version,
        status: nextStatus,
        versionId,
        vulnerabilityId,
      });

      if (result.error) {
        raiseToast.error(unwrapQueryErrorMessage(result.error));
      }
    },
    [
      annotationId,
      appId,
      openDelete,
      packageRef,
      upsertAnnotation,
      versionId,
      vulnerabilityId,
    ],
  );

  const handleEditClick = useCallback(() => {
    openUpsert({ annotationId, appId, packageRef, versionId, vulnerabilityId });
  }, [annotationId, appId, openUpsert, packageRef, versionId, vulnerabilityId]);

  const showsDetailsPopup = !canEdit && canViewDetails && isAnnotated;
  const buttonInteractive = canEdit || showsDetailsPopup;

  const buttonAriaLabel = isAnnotated
    ? 'Edit VEX Annotation'
    : 'Add VEX Annotation';

  const ButtonIcon = isAnnotated ? MessageCircleMore : MessageCirclePlus;

  const buttonHoverLabel = canEdit
    ? isAnnotated
      ? 'Edit annotation'
      : 'Add annotation'
    : showsDetailsPopup
      ? 'View annotation'
      : EDIT_DENIED_TOOLTIP;

  const [isDetailsPopupOpen, setIsDetailsPopupOpen] = useState(false);

  const dropdown = (
    <Popup
      content={EDIT_DENIED_TOOLTIP}
      disabled={canEdit}
      on="hover"
      trigger={
        // SUI Popup needs a non-disabled wrapper for hover events.
        <span className={s.dropdown}>
          <Dropdown
            aria-label="VEX Status"
            className={s.dropdown}
            disabled={!canEdit || isUpserting}
            loading={isUpserting}
            onChange={handleStatusChange}
            options={VEX_STATUS_OPTIONS}
            placeholder="None"
            // SUI's Dropdown value type rejects `null` even though the runtime
            // accepts it; the cast preserves the "None" sentinel that maps to
            // `null` in our option list.
            value={(pkg.vexStatus ?? null) as unknown as undefined}
            floating
            fluid
            selection
          />
        </span>
      }
    />
  );

  const button = (
    <Popup
      className={s.detailsPopup}
      content={
        <PopupPanel
          extra={
            <div className={s.popupSubheader}>
              <div className={s.cve}>{vulnerabilityId}</div>
              <div className={s.package}>
                Package: {pkg.name} {pkg.version}
              </div>
            </div>
          }
          icon={<Flame color="var(--sui-color-teal)" />}
          title="Vulnerability Exploit Statement"
        >
          <div className={s.detailsBody}>
            <VulnerabilityAnnotationDetails
              annotationId={annotationId!}
              appId={appId}
              versionId={versionId}
            />
          </div>
        </PopupPanel>
      }
      disabled={!showsDetailsPopup}
      on="click"
      onClose={() => setIsDetailsPopupOpen(false)}
      onOpen={() => setIsDetailsPopupOpen(true)}
      trigger={
        // Outer span gives the click popup a real DOM element to attach
        // its handler to; nesting `Popup` directly inside `Popup` doesn't
        // propagate the click event through SUI's wiring.
        <span>
          <Popup
            content={buttonHoverLabel}
            disabled={isDetailsPopupOpen}
            on="hover"
            position="left center"
            role="tooltip"
            size="mini"
            trigger={
              // SUI Popup needs a non-disabled wrapper for hover events.
              <span>
                <Button
                  aria-label={buttonAriaLabel}
                  className={classNames(s.iconButton, {
                    [s.iconButtonActive]: isAnnotated,
                  })}
                  disabled={!buttonInteractive}
                  onClick={canEdit ? handleEditClick : undefined}
                  basic
                  icon
                >
                  <ButtonIcon />
                </Button>
              </span>
            }
          />
        </span>
      }
      wide="very"
    />
  );

  return (
    <div className={s.row}>
      <span className={s.label}>VEX Status:</span>
      {dropdown}
      {button}
    </div>
  );
};

export default VulnerabilityAnnotationControls;
